import React, { useState, useMemo } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { Search, Home, Network, Building2, Users, Briefcase, FileText, ClipboardList, GraduationCap, Settings, HelpCircle, Mail } from "lucide-react";
import { cn } from "../components/ui/utils";

interface HelpArticle {
  id: string;
  question: string;
  answer: string;
  module: string;
  keywords: string[];
}

const helpArticles: HelpArticle[] = [
  // Dashboard
  {
    id: "dashboard-1",
    question: "What does the Dashboard show me?",
    answer: "The Dashboard provides an overview of your system with key statistics including people, departments, procedures, and activities. It shows recent items you have access to based on your role and department.",
    module: "Dashboard",
    keywords: ["overview", "statistics", "recent", "access"]
  },
  {
    id: "dashboard-2",
    question: "How do I filter Dashboard content by department?",
    answer: "If you're a Manager with access to multiple departments, you'll see a Department filter dropdown at the top right. Select 'All Departments' or choose a specific department to filter the displayed content.",
    module: "Dashboard",
    keywords: ["filter", "department", "manager", "dropdown"]
  },

  // Organization
  {
    id: "organization-1",
    question: "How do I view the organizational structure?",
    answer: "Go to Organization to see the full organizational chart. This shows the hierarchy of departments and roles within your organization in a visual format.",
    module: "Organisation",
    keywords: ["structure", "hierarchy", "chart", "visual"]
  },
  {
    id: "organization-2",
    question: "Can I see reporting lines in the organization chart?",
    answer: "Yes, the organization chart displays reporting relationships between roles and departments, helping you understand the management structure.",
    module: "Organisation",
    keywords: ["reporting", "management", "relationships", "roles"]
  },

  // Departments
  {
    id: "departments-1",
    question: "How do I create a new department?",
    answer: "Go to Departments, click 'Add Department', enter the department name, code, and description. Select a department head if needed, then save.",
    module: "Departments",
    keywords: ["create", "add", "new", "name", "code", "head"]
  },
  {
    id: "departments-2",
    question: "How do I assign a department head?",
    answer: "When creating or editing a department, use the Department Head dropdown to select from available users. Only users with appropriate permissions can be assigned as department heads.",
    module: "Departments",
    keywords: ["assign", "head", "manager", "edit", "permissions"]
  },
  {
    id: "departments-3",
    question: "Can I archive or delete a department?",
    answer: "You can archive departments that are no longer active but contain historical data. Only departments with no active procedures or users can be fully deleted.",
    module: "Departments",
    keywords: ["archive", "delete", "historical", "active", "procedures"]
  },

  // People
  {
    id: "people-1",
    question: "How do I add a new person to the system?",
    answer: "Go to People, click 'Add Person', fill in their details including name, email, role, and department. Set their initial password and permissions, then save.",
    module: "People",
    keywords: ["add", "new", "person", "details", "email", "role", "password"]
  },
  {
    id: "people-2",
    question: "How do I change someone's role or department?",
    answer: "Go to People, find the person, click Edit. Update their User Role and/or Department from the dropdowns. This will automatically adjust their system permissions.",
    module: "People",
    keywords: ["change", "role", "department", "edit", "permissions"]
  },
  {
    id: "people-3",
    question: "What's the difference between user roles?",
    answer: "Admin has full system access. Director can manage multiple departments. Manager can manage their assigned department(s). Worker has read access to their department's content.",
    module: "People",
    keywords: ["roles", "admin", "director", "manager", "worker", "permissions"]
  },
  {
    id: "people-4",
    question: "How do I reset someone's password?",
    answer: "Go to People, find the person, click Edit, then click 'Reset Password'. You can set a temporary password that they'll need to change on first login.",
    module: "People",
    keywords: ["reset", "password", "temporary", "login", "change"]
  },

  // Job Roles
  {
    id: "jobroles-1",
    question: "How do I create a job role?",
    answer: "Go to Job Roles, click 'Add Job Role', enter the role title, department, and description. Add any required skills, qualifications, and responsibilities, then save.",
    module: "Job Roles",
    keywords: ["create", "add", "title", "department", "skills", "qualifications"]
  },
  {
    id: "jobroles-2",
    question: "Can I assign procedures to job roles?",
    answer: "Yes, when creating or editing procedures, you can assign them to specific job roles. This helps define which roles are responsible for each procedure.",
    module: "Job Roles",
    keywords: ["assign", "procedures", "responsible", "edit", "define"]
  },


  // Procedures
  {
    id: "procedures-1",
    question: "How do I create a new procedure?",
    answer: "Go to Procedures, click 'Add Procedure', enter the title, select the type (Human, Agentic, or Hybrid), add steps, assign to job roles, and save.",
    module: "Procedures",
    keywords: ["create", "add", "title", "type", "steps", "assign"]
  },
  {
    id: "procedures-2",
    question: "What's the difference between Human, Agentic, and Hybrid procedures?",
    answer: "Human procedures require manual action by people. Agentic procedures are automated by systems. Hybrid procedures involve both human and automated elements working together.",
    module: "Procedures",
    keywords: ["human", "agentic", "hybrid", "manual", "automated", "systems"]
  },
  {
    id: "procedures-3",
    question: "Can I assign a procedure to multiple job roles?",
    answer: "Yes, when creating or editing a procedure, you can select multiple job roles in the assignment section. This makes the procedure available to all selected roles.",
    module: "Procedures",
    keywords: ["assign", "multiple", "job roles", "edit", "available"]
  },
  {
    id: "procedures-4",
    question: "How do I add attachments to procedures?",
    answer: "When creating or editing a procedure, use the attachments section to upload files, images, or documents that support the procedure steps.",
    module: "Procedures",
    keywords: ["attachments", "upload", "files", "images", "documents"]
  },
  {
    id: "procedures-5",
    question: "Can I track who has completed a procedure?",
    answer: "Yes, the system tracks procedure completion through Activities. You can see who performed procedures and when in the Activities section.",
    module: "Procedures",
    keywords: ["track", "completed", "activities", "performed", "history"]
  },

  // Activities
  {
    id: "activities-1",
    question: "What are Activities?",
    answer: "Activities are records of procedures that have been performed. They track who did what, when, and any outcomes or notes from the procedure execution.",
    module: "Activities",
    keywords: ["records", "performed", "track", "outcomes", "execution"]
  },
  {
    id: "activities-2",
    question: "How do I log a completed activity?",
    answer: "Go to Activities, click 'Log Activity', select the procedure, add details about the execution, attach any relevant files, and save the record.",
    module: "Activities",
    keywords: ["log", "completed", "select", "execution", "details", "files"]
  },
  {
    id: "activities-3",
    question: "Can I search and filter activities?",
    answer: "Yes, you can search activities by procedure name, performer, date range, or department. Use the filters to find specific activity records quickly.",
    module: "Activities",
    keywords: ["search", "filter", "procedure", "performer", "date", "department"]
  },

  // Training Plans
  {
    id: "training-1",
    question: "How do I create a training plan?",
    answer: "Go to Training Plans, click 'Add Training Plan', enter the plan name, description, select target job roles, add training modules or procedures, and set deadlines.",
    module: "Training Plans",
    keywords: ["create", "add", "name", "description", "job roles", "modules", "deadlines"]
  },
  {
    id: "training-2",
    question: "Can I assign training plans to specific people?",
    answer: "Yes, you can assign training plans to individual users or to entire job roles. People will see their assigned training in their personal dashboard.",
    module: "Training Plans",
    keywords: ["assign", "people", "users", "job roles", "personal", "dashboard"]
  },
  {
    id: "training-3",
    question: "How do I track training completion?",
    answer: "Training Plans show completion status for each assigned person. You can see who has completed training, who is in progress, and who hasn't started.",
    module: "Training Plans",
    keywords: ["track", "completion", "status", "progress", "assigned"]
  },

  // Settings
  {
    id: "settings-1",
    question: "What can I configure in Settings?",
    answer: "Settings allows you to manage system preferences, user permissions, department configurations, and system-wide policies. Available options depend on your user role.",
    module: "Settings",
    keywords: ["configure", "preferences", "permissions", "configurations", "policies"]
  },
  {
    id: "settings-2",
    question: "How do I manage user permissions?",
    answer: "In Settings, go to User Management to adjust role-based permissions, department access rights, and feature availability for different user types.",
    module: "Settings",
    keywords: ["manage", "permissions", "role-based", "access rights", "features"]
  }
];

const modules = [
  { id: "all", name: "All", icon: HelpCircle },
  { id: "Dashboard", name: "Dashboard", icon: Home },
  { id: "Organisation", name: "Organisation", icon: Network },
  { id: "Departments", name: "Departments", icon: Building2 },
  { id: "People", name: "People", icon: Users },
  { id: "Job Roles", name: "Job Roles", icon: Briefcase },
  { id: "Procedures", name: "Procedures", icon: FileText },
  { id: "Activities", name: "Activities", icon: ClipboardList },
  { id: "Training Plans", name: "Training Plans", icon: GraduationCap },
  { id: "Settings", name: "Settings", icon: Settings }
];

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModule, setSelectedModule] = useState("all");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter articles based on search and module
  const filteredArticles = useMemo(() => {
    let filtered = helpArticles;

    // Filter by module
    if (selectedModule !== "all") {
      filtered = filtered.filter(article => article.module === selectedModule);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.question.toLowerCase().includes(query) ||
        article.answer.toLowerCase().includes(query) ||
        article.module.toLowerCase().includes(query) ||
        article.keywords.some(keyword => keyword.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [searchQuery, selectedModule]);

  // Get search suggestions
  const suggestions = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    const matches = helpArticles.filter(article =>
      article.question.toLowerCase().includes(query) ||
      article.keywords.some(keyword => keyword.toLowerCase().includes(query))
    );

    return matches.slice(0, 5);
  }, [searchQuery]);

  const handleSuggestionClick = (article: HelpArticle) => {
    setSearchQuery(article.question);
    setSelectedModule(article.module);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Help Centre</h1>
        <p className="text-muted-foreground">Find answers to common questions about using idarah.io</p>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10"
          />
        </div>

        {/* Search Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
            {suggestions.map((article) => (
              <button
                key={article.id}
                onClick={() => handleSuggestionClick(article)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{article.question}</span>
                  <Badge variant="secondary" className="text-xs">
                    {article.module}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Module Filter Buttons */}
      <div className="flex flex-wrap gap-2 justify-center">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Button
              key={module.id}
              variant={selectedModule === module.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedModule(module.id)}
              className={cn(
                "flex items-center gap-2",
                selectedModule === module.id && "bg-black text-white"
              )}
            >
              <Icon className="h-4 w-4" />
              {module.name}
            </Button>
          );
        })}
      </div>

      {/* Results Summary */}
      <div className="text-center text-muted-foreground">
        Showing {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
        {selectedModule !== "all" && ` in ${modules.find(m => m.id === selectedModule)?.name}`}
        {searchQuery.trim() && ` matching "${searchQuery}"`}
      </div>

      {/* Help Articles */}
      <div className="max-w-4xl mx-auto">
        {filteredArticles.length > 0 ? (
          <Accordion type="single" collapsible className="space-y-4">
            {filteredArticles.map((article) => (
              <AccordionItem key={article.id} value={article.id} className="border rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center justify-between w-full mr-4">
                    <span className="font-medium">{article.question}</span>
                    <Badge variant="outline" className="ml-2">
                      {article.module}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {article.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search terms or selecting a different module.
              </p>
              <Button variant="outline" onClick={() => { setSearchQuery(""); setSelectedModule("all"); }}>
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Contact Support */}
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Mail className="h-5 w-5" />
              Can't find what you're looking for?
            </CardTitle>
            <CardDescription>
              Our support team is here to help you get the most out of idarah.io
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button>
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 